import React, { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { api } from '../lib/api';
import { useAuthStore } from '../stores/authStore';
import { AppCredentialsManager } from '../components/AppCredentialsManager';
import { SubscriptionReceiptModal } from '../components/SubscriptionReceiptModal';
import type { Subscription } from '../types';
import {
  ArrowLeft,
  ArrowRight,
  CreditCard,
  Layers,
  Key,
  Gauge,
  Clock,
  Calendar,
  DollarSign,
  FileCheck,
} from 'lucide-react';

export const SubscriptionDetails: React.FC = () => {
  const { subId, id } = useParams<{ subId?: string; id?: string }>();
  const subscriptionId = subId || id;
  const navigate = useNavigate();
  const client = useAuthStore((state) => state.client);
  const [isReceiptOpen, setIsReceiptOpen] = useState(false);

  const { data: subscription, isLoading } = useQuery({
    queryKey: ['subscription', subscriptionId],
    queryFn: async () => {
      const response = await api.get<{ data: Subscription }>(`/subscriptions/${subscriptionId}`);
      return response.data.data;
    },
    enabled: !!subscriptionId,
  });

  if (isLoading) {
    return (
      <div className="text-center py-12 animate-fade-in">
        <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
        <p className="mt-2 text-slate-600 dark:text-slate-400 text-sm">A carregar detalhes da subscrição...</p>
      </div>
    );
  }

  if (!subscription) {
    return (
      <div className="text-center py-12 bg-white dark:bg-slate-900 rounded-2xl border border-slate-200 dark:border-slate-800 p-8">
        <p className="text-slate-600 dark:text-slate-400 mb-4 text-base">Subscrição não encontrada ou expirada.</p>
        <button onClick={() => navigate(-1)} className="btn-base btn-primary inline-flex items-center gap-1.5">
          <ArrowLeft className="w-4 h-4" />
          <span>Voltar às Subscrições</span>
        </button>
      </div>
    );
  }

  const handleBack = () => {
    if (subscription.application_id) {
      navigate(`/applications/${subscription.application_id}/subscriptions`);
    } else {
      navigate(-1);
    }
  };

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'active':
        return 'bg-emerald-50 text-emerald-700 border-emerald-200 dark:bg-emerald-950/40 dark:text-emerald-400 dark:border-emerald-800/50';
      case 'cancelled':
        return 'bg-amber-50 text-amber-700 border-amber-200 dark:bg-amber-950/40 dark:text-amber-400 dark:border-amber-800/50';
      case 'expired':
      default:
        return 'bg-rose-50 text-rose-700 border-rose-200 dark:bg-rose-950/40 dark:text-rose-400 dark:border-rose-800/50';
    }
  };

  return (
    <div className="animate-fade-in space-y-6">
      <div>
        <button
          onClick={handleBack}
          className="text-primary-600 dark:text-primary-400 hover:text-primary-700 dark:hover:text-primary-300 mb-3 flex items-center gap-1.5 text-sm font-semibold transition-colors"
        >
          <ArrowLeft className="w-4 h-4" />
          <span>Voltar às Subscrições</span>
        </button>
        <h1 className="text-3xl font-bold text-slate-900 dark:text-white flex items-center gap-2.5">
          <CreditCard className="w-8 h-8 text-primary-500" />
          <span>Detalhes da Subscrição</span>
        </h1>
      </div>

      <div className="bg-white dark:bg-slate-900 rounded-2xl border border-slate-200/80 dark:border-slate-800 shadow-xs p-6 sm:p-8">
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-6 pb-6 border-b border-slate-100 dark:border-slate-800">
          <div>
            <h2 className="text-2xl font-bold text-slate-900 dark:text-white">
              {subscription.product?.name || 'Produto Desconhecido'}
            </h2>
            <div className="flex items-center gap-2 mt-2">
              <span
                className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-bold border capitalize ${getStatusBadge(
                  subscription.status
                )}`}
              >
                {subscription.status === 'active'
                  ? 'Ativa'
                  : subscription.status === 'cancelled'
                    ? 'Cancelada'
                    : 'Expirada'}
              </span>
            </div>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <button
              onClick={() => setIsReceiptOpen(true)}
              className="btn-base btn-sm btn-secondary flex items-center gap-1.5 font-semibold text-slate-700 dark:text-slate-300"
              title="Emitir Comprovativo / Fatura Proforma"
            >
              <FileCheck className="w-4 h-4 text-emerald-600 dark:text-emerald-400" />
              <span>Ver Recibo Oficial</span>
            </button>
            {subscription.application_id && (
              <button
                onClick={() =>
                  navigate(
                    `/applications/${subscription.application_id}/products/${subscription.product_id}`
                  )
                }
                className="btn-base btn-sm btn-secondary flex items-center gap-1.5 border border-slate-200 dark:border-slate-700 text-primary-600 dark:text-primary-400 font-semibold"
              >
                <span>Ver Documentação & Testes</span>
                <ArrowRight className="w-4 h-4" />
              </button>
            )}
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 mb-6">
          {/* Application Information */}
          {subscription.application && (
            <>
              <div className="bg-slate-50 dark:bg-slate-800/50 p-4 rounded-xl border border-slate-100 dark:border-slate-700/80 flex items-start gap-3">
                <div className="p-2 rounded-lg bg-primary-50 dark:bg-primary-950/40 text-primary-600 dark:text-primary-400 shrink-0">
                  <Layers className="w-4 h-4" />
                </div>
                <div>
                  <p className="text-2xs text-slate-400 dark:text-slate-500 uppercase font-bold tracking-wider mb-0.5">
                    Aplicação Vinculada
                  </p>
                  <p className="font-semibold text-slate-900 dark:text-white text-sm">
                    {subscription.application.name}
                  </p>
                </div>
              </div>

              <div className="bg-slate-50 dark:bg-slate-800/50 p-4 rounded-xl border border-slate-100 dark:border-slate-700/80 flex items-start gap-3">
                <div className="p-2 rounded-lg bg-primary-50 dark:bg-primary-950/40 text-primary-600 dark:text-primary-400 shrink-0">
                  <Key className="w-4 h-4" />
                </div>
                <div>
                  <p className="text-2xs text-slate-400 dark:text-slate-500 uppercase font-bold tracking-wider mb-0.5">
                    ID da Aplicação
                  </p>
                  <p className="font-mono text-2xs text-slate-700 dark:text-slate-300 truncate max-w-[180px]">
                    {subscription.application.id}
                  </p>
                </div>
              </div>
            </>
          )}

          {/* Plan Information */}
          {subscription.plan && (
            <>
              <div className="bg-slate-50 dark:bg-slate-800/50 p-4 rounded-xl border border-slate-100 dark:border-slate-700/80 flex items-start gap-3">
                <div className="p-2 rounded-lg bg-emerald-50 dark:bg-emerald-950/40 text-emerald-600 dark:text-emerald-400 shrink-0">
                  <CreditCard className="w-4 h-4" />
                </div>
                <div>
                  <p className="text-2xs text-slate-400 dark:text-slate-500 uppercase font-bold tracking-wider mb-0.5">
                    Plano Atual
                  </p>
                  <p className="font-semibold text-slate-900 dark:text-white capitalize text-sm">
                    {subscription.plan.tier}
                  </p>
                </div>
              </div>

              <div className="bg-slate-50 dark:bg-slate-800/50 p-4 rounded-xl border border-slate-100 dark:border-slate-700/80 flex items-start gap-3">
                <div className="p-2 rounded-lg bg-sky-50 dark:bg-sky-950/40 text-sky-600 dark:text-sky-400 shrink-0">
                  <Gauge className="w-4 h-4" />
                </div>
                <div>
                  <p className="text-2xs text-slate-400 dark:text-slate-500 uppercase font-bold tracking-wider mb-0.5">
                    Cota Mensal
                  </p>
                  <p className="font-semibold text-slate-900 dark:text-white text-sm">
                    {subscription.plan.monthly_quota.toLocaleString()} chamadas
                  </p>
                </div>
              </div>

              <div className="bg-slate-50 dark:bg-slate-800/50 p-4 rounded-xl border border-slate-100 dark:border-slate-700/80 flex items-start gap-3">
                <div className="p-2 rounded-lg bg-amber-50 dark:bg-amber-950/40 text-amber-600 dark:text-amber-400 shrink-0">
                  <Clock className="w-4 h-4" />
                </div>
                <div>
                  <p className="text-2xs text-slate-400 dark:text-slate-500 uppercase font-bold tracking-wider mb-0.5">
                    Taxa Máxima
                  </p>
                  <p className="font-semibold text-slate-900 dark:text-white text-sm">
                    {subscription.plan.rate_limit_per_minute} req/min
                  </p>
                </div>
              </div>

              <div className="bg-slate-50 dark:bg-slate-800/50 p-4 rounded-xl border border-slate-100 dark:border-slate-700/80 flex items-start gap-3">
                <div className="p-2 rounded-lg bg-emerald-50 dark:bg-emerald-950/40 text-emerald-600 dark:text-emerald-400 shrink-0">
                  <DollarSign className="w-4 h-4" />
                </div>
                <div>
                  <p className="text-2xs text-slate-400 dark:text-slate-500 uppercase font-bold tracking-wider mb-0.5">
                    Tarifa Base
                  </p>
                  <p className="font-semibold text-slate-900 dark:text-white text-sm">
                    ${subscription.plan.monthly_price}/mês
                  </p>
                </div>
              </div>
            </>
          )}

          {/* Dates */}
          <div className="bg-slate-50 dark:bg-slate-800/50 p-4 rounded-xl border border-slate-100 dark:border-slate-700/80 flex items-start gap-3">
            <div className="p-2 rounded-lg bg-slate-100 dark:bg-slate-700 text-slate-500 dark:text-slate-400 shrink-0">
              <Calendar className="w-4 h-4" />
            </div>
            <div>
              <p className="text-2xs text-slate-400 dark:text-slate-500 uppercase font-bold tracking-wider mb-0.5">
                Data de Início
              </p>
              <p className="font-semibold text-slate-900 dark:text-white text-sm">
                {new Date(subscription.start_date).toLocaleDateString('pt-PT')}
              </p>
            </div>
          </div>

          {subscription.end_date && (
            <div className="bg-slate-50 dark:bg-slate-800/50 p-4 rounded-xl border border-slate-100 dark:border-slate-700/80 flex items-start gap-3">
              <div className="p-2 rounded-lg bg-slate-100 dark:bg-slate-700 text-slate-500 dark:text-slate-400 shrink-0">
                <Calendar className="w-4 h-4" />
              </div>
              <div>
                <p className="text-2xs text-slate-400 dark:text-slate-500 uppercase font-bold tracking-wider mb-0.5">
                  Data de Término
                </p>
                <p className="font-semibold text-slate-900 dark:text-white text-sm">
                  {new Date(subscription.end_date).toLocaleDateString('pt-PT')}
                </p>
              </div>
            </div>
          )}
        </div>

        {/* Credentials Manager */}
        {subscription.application_id && (
          <div className="border-t border-slate-100 dark:border-slate-800 pt-6 mt-6">
            <h3 className="text-xl font-bold text-slate-900 dark:text-white mb-4">
              Credenciais da API Externa
            </h3>
            <AppCredentialsManager
              applicationId={subscription.application_id}
              clientId={subscription.client_id || client?.id || ''}
              productId={subscription.product_id}
              authProvider={subscription.product?.authentication_provider || 'BASIC_AUTH'}
            />
          </div>
        )}
      </div>

      <SubscriptionReceiptModal
        isOpen={isReceiptOpen}
        onClose={() => setIsReceiptOpen(false)}
        subscription={subscription}
        clientName={client?.company_name}
        clientEmail={client?.email}
      />
    </div>
  );
};
